import { useState } from 'react';

import { Box, Grid, Stack, styled, Typography } from '@mui/material';
import { Bell, CaretDoubleDown, CaretDown, CaretUp } from 'phosphor-react';

import { useNotifications } from '~components/hooks/notifications/useNotifications';
import { createFadeSlideAnimation } from '~components/theme/styles/animations';
import { black, primaryColors } from '~components/theme/styles/colors';
import { columnGap } from '~components/theme/styles/mixins';

import { AccountDialBridges } from '../../ui/accountDial/content/bridges';
import { AccountDialConnection } from '../../ui/accountDial/content/connection';
import { AccountDialNetworks } from '../../ui/accountDial/content/networkSwitch';
import { AccountDialSettings } from '../../ui/accountDial/content/settings';
import { AccountDialSystemStats } from '../../ui/accountDial/content/systemStats';
import { AccountDialTransactions } from '../../ui/accountDial/content/transactions';

const MobileNoficationPanel = styled(Grid, { shouldForwardProp: prop => prop !== 'read' })<{
  read?: boolean;
}>(({ read }) => ({
  padding: '24px 32px',
  background: black['100%'],
  borderRadius: '8px',
  width: '100%',
  '&:after':
    !read && typeof read !== undefined
      ? {
          content: `""`,
          borderRadius: '50%',
          width: '16px',
          height: '16px',
          background: 'red',
          lineHeight: '16px',
          position: 'absolute',
          top: '-4px',
          left: '-4px',
          fontWeight: 500,
          textAlign: 'center',
          animation: createFadeSlideAnimation(0),
        }
      : undefined,
}));

export const MobileAccountPanel: React.FC<{ setIsOpen: (open: boolean) => void }> = ({ setIsOpen }) => {
  const { notificationsComponents, setNotificationRead, notificationsStatus } = useNotifications();
  const [selectedNotification, setSelectedNotification] = useState<number>(0);

  function nextNotification() {
    if (notificationsComponents.length - 1 >= selectedNotification + 1) {
      setNotificationRead(selectedNotification);
      setSelectedNotification(selectedNotification + 1);
    }
  }

  function prevNotification() {
    if (selectedNotification - 1 >= 0) {
      setNotificationRead(selectedNotification);
      setSelectedNotification(selectedNotification - 1);
    }
  }

  return (
    <Grid sx={{ padding: '32px 24px', mr: 2, width: '100%' }}>
      <Grid sx={{ ...columnGap(32) }}>
        <Grid sx={{ ...columnGap(16) }}>
          <Grid
            container
            sx={{ position: 'relative', alignItems: 'center', justifyContent: 'space-between' }}
          >
            <Typography variant="label3">NOTIFICATIONS</Typography>
            <Bell size={16} />
          </Grid>
          <Grid sx={{ position: 'relative' }}>
            <MobileNoficationPanel read={notificationsStatus[selectedNotification]}>
              <Stack direction={'row'} justifyContent={'space-between'} width={'100%'}>
                {notificationsComponents[selectedNotification]}
                <Stack>
                  <Box
                    onClick={prevNotification}
                    sx={{
                      transition: 'color 0.2s ease',
                      color: primaryColors[600],
                      '& :hover': { color: primaryColors[200] },
                    }}
                  >
                    <CaretUp size={16} />
                  </Box>
                  <Box
                    onClick={nextNotification}
                    sx={{
                      transition: 'color 0.2s ease',
                      color: primaryColors[600],
                      '& :hover': { color: primaryColors[200] },
                    }}
                  >
                    <CaretDown size={16} />
                  </Box>
                </Stack>
              </Stack>
            </MobileNoficationPanel>
          </Grid>
          <Grid
            container
            sx={{
              position: 'relative',
              alignItems: 'center',
              justifyContent: 'space-between',
              userSelect: 'none',
            }}
          >
            <Typography variant="label3" sx={{ color: primaryColors[800] }}>
              SEE MORE
            </Typography>
            <CaretDoubleDown size={16} style={{ color: primaryColors[800] }} />
          </Grid>
        </Grid>
        <Grid sx={{ ...columnGap(8) }}>
          <AccountDialConnection
            onConnect={() => {
              setIsOpen(false);
            }}
          />
          <AccountDialNetworks />
          <AccountDialBridges />
          <AccountDialTransactions />
          <AccountDialSettings />
          <AccountDialSystemStats />
        </Grid>
      </Grid>
    </Grid>
  );
};
